import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, notification } from 'antd';
import moment from 'moment';
import APIClient from '../api/api';
import { InboundEmail, InboundEmailAttachment } from '../api/models';

const MIN_WIDTH = 480;
const MIN_HEIGHT = 400;

interface Props {
  emailId: number;
  onClose: () => void;
  onTagsChanged?: () => void;
  onPrev?: () => void;
  onNext?: () => void;
}

function extractHtmlFromPayload(payload: string | null): string | null {
  if (!payload) return null;
  try {
    const json = atob(payload);
    const parsed = JSON.parse(json);
    const html = parsed?.ParsedValues?.html;
    return typeof html === 'string' && html.trim() ? html : null;
  } catch {
    return null;
  }
}

export default function EmailDetailModal({ emailId, onClose, onTagsChanged, onPrev, onNext }: Props) {
  const [email, setEmail] = useState<InboundEmail | null>(null);
  const [loading, setLoading] = useState(true);
  const [removingTag, setRemovingTag] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'html' | 'text'>('html');
  const [modalSize, setModalSize] = useState({ width: Math.round(window.innerWidth * 0.75), height: Math.round(window.innerHeight * 0.75) });
  const resizing = useRef(false);

  useEffect(() => {
    loadEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailId]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        onPrev?.();
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        onNext?.();
      }
    };
    if (onPrev || onNext) {
      window.addEventListener('keydown', handler);
      return () => window.removeEventListener('keydown', handler);
    }
  }, [onPrev, onNext]);

  async function loadEmail() {
    setLoading(true);
    try {
      const resp = await APIClient.getEmail(emailId);
      setEmail(resp);
    } catch {
      notification.error({ message: 'Failed to load email.' });
    } finally {
      setLoading(false);
    }
  }

  const htmlBody = email ? extractHtmlFromPayload(email.payload) : null;
  const hasHtml = htmlBody !== null;

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    resizing.current = true;
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = modalSize.width;
    const startH = modalSize.height;

    const onMove = (ev: MouseEvent) => {
      if (!resizing.current) return;
      const newW = Math.max(MIN_WIDTH, startW + (ev.clientX - startX) * 2); // *2 because centered
      const newH = Math.max(MIN_HEIGHT, startH + (ev.clientY - startY));
      setModalSize({ width: newW, height: newH });
    };
    const onUp = () => {
      resizing.current = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [modalSize]);

  async function removeTag(tag: string) {
    setRemovingTag(tag);
    try {
      await APIClient.deleteEmailTag(emailId, tag);
      await loadEmail();
      onTagsChanged?.();
    } catch {
      notification.error({ message: 'Failed to remove tag.' });
    } finally {
      setRemovingTag(null);
    }
  }

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      width={modalSize.width}
      closable={false}
      destroyOnClose
      styles={{
        content: { padding: 0, background: 'rgb(var(--color-panel) / 1)', borderRadius: 10, overflow: 'hidden', height: modalSize.height, display: 'flex', flexDirection: 'column', position: 'relative' },
        body: { padding: 0, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' },
      }}
    >
      {loading && !email && (
        <div className="px-6 py-12 text-center text-text-3 text-[12.5px]">Loading…</div>
      )}
      {email && (
        <>
          {/* Header */}
          <div className="px-6 pt-5 pb-4 border-b border-border">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="text-[15px] font-semibold text-text leading-snug">
                  {email.subject || <span className="text-text-3">(no subject)</span>}
                </div>
                <div className="flex items-center gap-2 mt-2 text-[12px] text-text-3">
                  <span className="font-mono">{moment(email.sentAt).format('MMM D, YYYY · HH:mm')}</span>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {(onPrev || onNext) && (
                  <div className="flex items-center mr-1">
                    <button
                      type="button"
                      onClick={onPrev}
                      disabled={!onPrev}
                      aria-label="Previous email"
                      title="Previous email (↑)"
                      className="w-[28px] h-[28px] rounded-l-md border border-border bg-panel flex items-center justify-center cursor-pointer text-text-3 hover:bg-bg-elevated hover:text-text disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={onNext}
                      disabled={!onNext}
                      aria-label="Next email"
                      title="Next email (↓)"
                      className="w-[28px] h-[28px] rounded-r-md border border-l-0 border-border bg-panel flex items-center justify-center cursor-pointer text-text-3 hover:bg-bg-elevated hover:text-text disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      ↓
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => {
                    const url = new URL(window.location.href);
                    url.searchParams.set('email', String(emailId));
                    navigator.clipboard.writeText(url.toString());
                    notification.success({ message: 'Link copied.' });
                  }}
                  aria-label="Copy link"
                  title="Copy link"
                  className="w-[28px] h-[28px] rounded-md border border-border bg-panel flex items-center justify-center cursor-pointer text-text-3 hover:bg-bg-elevated hover:text-text"
                >
                  ⎘
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className="w-[28px] h-[28px] rounded-md border border-border bg-panel flex items-center justify-center cursor-pointer text-text-3 hover:bg-bg-elevated hover:text-text"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Tags */}
            {email.tags && email.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {email.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-1.5 py-px rounded text-[10.5px] font-medium bg-accent-soft text-accent"
                  >
                    {tag}
                    <button
                      type="button"
                      disabled={removingTag === tag}
                      onClick={() => removeTag(tag)}
                      className="bg-transparent border-0 p-0 cursor-pointer text-accent/50 hover:text-accent leading-none text-[10px] disabled:opacity-30"
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Meta */}
          <div className="px-6 py-2 border-b border-border bg-bg flex flex-col gap-0.5 text-[12px]">
            <div className="flex gap-2">
              <span className="text-text-3 w-[36px] shrink-0 text-right">From</span>
              <EmailAddress value={email.from} />
            </div>
            <div className="flex gap-2">
              <span className="text-text-3 w-[36px] shrink-0 text-right">To</span>
              {email.to.length === 0
                ? <span className="text-text-3">Unknown</span>
                : <EmailAddress value={email.to} />
              }
            </div>
            {email.venue && (
              <div className="flex gap-2">
                <span className="text-text-3 w-[36px] shrink-0 text-right">Venue</span>
                <span className="text-text-2">
                  {email.venue}
                  {email.account && <span className="text-text-3"> · {email.account}</span>}
                </span>
              </div>
            )}
          </div>

          {/* Attachments */}
          {email.attachments && email.attachments.length > 0 && (
            <div className="px-6 py-2.5 border-b border-border bg-bg flex items-center gap-3 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider text-text-3 font-semibold">Attachments</span>
              {email.attachments.map((a: InboundEmailAttachment) => (
                <a
                  key={a.id}
                  target="_blank"
                  rel="noreferrer"
                  href={APIClient.getAttachmentDownloadUrl(a)}
                  className="text-accent text-[12px] hover:opacity-80 font-mono"
                >
                  {a.filename}
                </a>
              ))}
            </div>
          )}

          {/* Body */}
          <div className="flex flex-col flex-1 min-h-0">
            {hasHtml && (
              <div className="px-6 pt-3 pb-1 flex gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode('html')}
                  className={`px-2 py-0.5 rounded text-[11px] font-medium border cursor-pointer ${
                    viewMode === 'html'
                      ? 'bg-accent-soft text-accent border-accent/30'
                      : 'bg-transparent text-text-3 border-border hover:text-text-2'
                  }`}
                >
                  HTML
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('text')}
                  className={`px-2 py-0.5 rounded text-[11px] font-medium border cursor-pointer ${
                    viewMode === 'text'
                      ? 'bg-accent-soft text-accent border-accent/30'
                      : 'bg-transparent text-text-3 border-border hover:text-text-2'
                  }`}
                >
                  Plain text
                </button>
              </div>
            )}
            <div className="px-6 py-5 flex-1 min-h-0 overflow-auto">
              {hasHtml && viewMode === 'html' ? (
                <div className="rounded-md overflow-hidden border border-border" style={{ height: '100%' }}>
                  <iframe
                    sandbox=""
                    srcDoc={`<!DOCTYPE html><html><head><style>body{margin:0;padding:8px;font-family:-apple-system,BlinkMacSystemFont,sans-serif;font-size:13px;line-height:1.5;color:#222;background:#fff;word-break:break-word}img{max-width:100%;height:auto}table{max-width:100%}</style></head><body>${htmlBody}</body></html>`}
                    style={{ width: '100%', height: '100%', border: 'none' }}
                    title="Email body"
                  />
                </div>
              ) : (
                <pre className="whitespace-pre-wrap text-text text-[12.5px] font-sans m-0 leading-relaxed">
                  {email.text}
                </pre>
              )}
            </div>
          </div>

          {/* Resize handle */}
          <div
            onMouseDown={handleResizeStart}
            className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize opacity-40 hover:opacity-80"
            style={{ touchAction: 'none' }}
          >
            <svg viewBox="0 0 16 16" fill="currentColor" className="text-text-3 w-full h-full">
              <path d="M14 14H12V12H14V14ZM14 10H10V12H12V14H14V10ZM14 6H6V8H8V10H10V12H12V14H14V6Z" fillRule="evenodd" />
            </svg>
          </div>
        </>
      )}
    </Modal>
  );
}

function parseEmailAddress(raw: string): { name: string; email: string } | null {
  // Match "Name <email@example.com>" or "Name<email@example.com>"
  const match = raw.match(/^(.+?)\s*<([^>]+)>$/);
  if (match) {
    const name = match[1].replace(/^["']|["']$/g, '').trim();
    const email = match[2].trim();
    if (name && email.includes('@')) return { name, email };
  }
  return null;
}

function EmailAddress({ value }: { value: string }) {
  const parsed = parseEmailAddress(value);
  if (parsed) {
    return (
      <div className="flex flex-col">
        <span className="text-text">{parsed.name}</span>
        <span className="text-text-3 font-mono text-[11px]">{parsed.email}</span>
      </div>
    );
  }
  return <span className="text-text-2 font-mono break-all">{value}</span>;
}
